import styled from 'styled-components';
import theme from '../theme';

export const PageWrapper = styled.div`
  font-family: ${theme.typography.fontFamily};
`;

export const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

export const PageTitle = styled.h1`
  font-size: ${theme.typography.h1.size};
  font-weight: ${theme.typography.h1.weight};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm};

  ${theme.media.mobile} {
    font-size: ${theme.typography.h2.size};
  }
`;

export const PageSubtitle = styled.p`
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

export const SectionHeader = styled.h2`
  font-size: ${theme.typography.h2.size};
  font-weight: ${theme.typography.h2.weight};
  color: ${theme.colors.text};
  margin: ${theme.spacing.xl} 0 ${theme.spacing.md};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 2px solid ${theme.colors.borderLight};

  &:first-of-type {
    margin-top: 0;
  }
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const SessionCard = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.lg};
  border-left: 4px solid ${({ $accent }) => $accent || theme.colors.primary};
  display: flex;
  flex-direction: column;
  transition: all ${theme.transitions.normal};

  &:hover {
    box-shadow: ${theme.shadows.md};
    transform: translateY(-2px);
  }
`;

export const CardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${theme.radii.sm};
  background: ${({ $bg }) => $bg || theme.colors.primaryGhost};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin-bottom: ${theme.spacing.md};
  flex-shrink: 0;
`;

export const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin: 0 0 ${theme.spacing.xs};
`;

export const CardTitle = styled.h3`
  font-size: ${theme.typography.h3.size};
  font-weight: ${theme.typography.h3.weight};
  color: ${theme.colors.text};
  margin: 0;
`;

export const InfoIconWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${theme.colors.textMuted};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  font-style: italic;
  font-family: Georgia, serif;
  cursor: help;
  flex-shrink: 0;

  &:hover + div {
    opacity: 1;
    visibility: visible;
  }
`;

export const InfoTooltip = styled.div`
  position: absolute;
  ${({ $below }) => $below
    ? 'top: calc(100% + 8px);'
    : 'bottom: calc(100% + 8px);'}
  left: 50%;
  transform: translateX(-50%);
  width: 280px;
  max-width: 90vw;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.text};
  color: #fff;
  font-size: ${theme.typography.small.size};
  font-weight: 400;
  line-height: 1.5;
  border-radius: ${theme.radii.sm};
  box-shadow: ${theme.shadows.md};
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s, visibility 0.15s;
  z-index: 1000;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    ${({ $below }) => $below
      ? `top: -6px; border-bottom: 6px solid ${theme.colors.text};`
      : `bottom: -6px; border-top: 6px solid ${theme.colors.text};`}
  }
`;

export const LevelBadge = styled.span`
  display: inline-block;
  font-size: ${theme.typography.tiny.size};
  font-weight: 600;
  color: ${({ $color }) => $color || theme.colors.textSecondary};
  background: ${({ $bg }) => $bg || theme.colors.surfaceHover};
  padding: 2px 8px;
  border-radius: ${theme.radii.full};
  margin-bottom: ${theme.spacing.sm};
  width: fit-content;
`;

export const StartButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};
  padding: 10px 20px;
  background: ${theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${theme.radii.sm};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  transition: background ${theme.transitions.fast};
  width: 100%;

  &:hover:not(:disabled) {
    background: ${theme.colors.primaryDark};
  }

  &:disabled {
    background: ${theme.colors.textMuted};
    cursor: not-allowed;
  }
`;

export const ErrorBanner = styled.div`
  background: ${theme.colors.dangerLight};
  color: ${theme.colors.danger};
  border: 1px solid ${theme.colors.danger};
  border-radius: ${theme.radii.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.small.size};
  font-weight: 500;
`;
